/**
 * Creates light theme colors object with dynamic mainColor and iconColor values.
 * This function allows colors to be reactive to theme changes.
 * 
 * @param mainColor - The main color (default: '#1976d2')
 * @param iconColor - The icon color (default: '#000000')
 * @returns Colors object with dynamic palette values
 */
export const createColors = (mainColor: string = '#1976d2', iconColor: string = '#000000') => {
  const palette = {
    neutral100: "#FFFFFF",
    neutral200: "#F4F2F1",
    neutral300: "#D7CEC9",
    neutral400: "#B6ACA6",
    neutral500: "#978F8A",
    neutral600: "#564E4A",
    neutral700: "#3C3836",
    neutral800: "#191015",
    neutral900: "#000000",

    overhearblue: "#214176",
    libraryblue: "#2f5ca6",

    primary100: "#F4E0D9",
    primary200: "#E8C1B4",
    primary300: "#DDA28E",
    primary400: "#D28468",
    primary500: "#C76542",
    primary600: "#A54F31",

    secondary100: "#DCDDE9",
    secondary200: "#BCC0D6",
    secondary300: "#9196B9",
    secondary400: "#626894",
    secondary500: "#41476E",

    accent100: "#FFEED4",
    accent200: "#FFE1B2",
    accent300: "#FDD495",
    accent400: "#FBC878",
    accent500: "#FFBB50",

    angry100: "#F2D6CD",
    angry500: "#C03403",

    overlay20: "rgba(25, 16, 21, 0.2)",
    overlay50: "rgba(25, 16, 21, 0.5)",

    main: mainColor,
    icon: iconColor,
  } as const

  return {
    /**
     * The palette is available to use, but prefer using the name.
     * This is only included for rare, one-off cases. Try to use
     * semantic names as much as possible.
     */
    palette: {
      ...palette,
      // Additional palette properties for web components
      border: palette.neutral400,
      input: {
        background: '#f5f5f5',
        text: '#000000',
      },
      button: {
        primary: mainColor,
        secondary: '#dc004e',
        text: '#ffffff',
      },
      dropdown: {
        background: '#f5f5f5',
        hover: '#e0e0e0',
        selected: '#bbdefb',
      },
    },
    /**
     * A helper for making something see-thru.
     */
    transparent: "rgba(0, 0, 0, 0)",
    /**
     * The default text color in many components.
     */
    text: palette.neutral800,
    /**
     * Secondary text information.
     */
    textDim: palette.neutral600,
    /**
     * The default color of the screen background.
     */
    background: palette.neutral200,
    /**
     * The default border color.
     */
    border: palette.neutral400,
    /**
     * The main tinting color.
     */
    tint: palette.primary500,
    /**
     * A subtle color used for lines.
     */
    separator: palette.neutral300,
    /**
     * Error messages.
     */
    error: palette.angry500,
    /**
     * Error Background.
     */
    errorBackground: palette.angry100,
    /**
     * Primary color (from mainColor parameter)
     */
    primary: mainColor,
    /**
     * Secondary color
     */
    secondary: "#dc004e",
  } as const
}

// Default export for backward compatibility
export const colors = createColors()
